import React from "react";
import {
  Box,
  Button,
  Stack,
  Typography,
  useTheme,
  alpha,
  IconButton,
} from "@mui/material";
import { useSelector, useDispatch } from "react-redux";
import { selectActiveCall } from "../../../state/callSessions/callSessionsSlice";
import { selectParkSpots } from "../../../state/callPark/callParkSlice";
import { showSnackbar } from "../../../state/snackbar/snackbarSlice";
import LocalParkingIcon from "@mui/icons-material/LocalParking";

const CallParkOverlay = ({ onClose }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const activeCall = useSelector(selectActiveCall);
  const parkSpots = useSelector(selectParkSpots);

  const parkQueues = parkSpots.map((spot) => ({
    id: spot.queue_name,
    number: spot.queue_name,
    status: spot.parkedCalls?.length > 0 ? "Occupied" : "Empty",
    description: spot.description,
  }));

  const handlePark = (queueNumber, description) => {
    if (activeCall) {
      activeCall.transfer(queueNumber, true);
      onClose();
      dispatch(
        showSnackbar({
          message: `${activeCall.displayName} placed on ${description || `Park ${queueNumber}`
            }`,
          severity: "success",
        })
      );
    }
  };

  const handleDynamicPark = () => {
    if (activeCall) {
      const emptySpot = parkSpots.find((spot) => !spot.parkedCalls?.length);
      if (emptySpot) {
        // Use the existing handlePark function with the empty spot
        handlePark(emptySpot.queue_name, emptySpot.description);
      } else {
        // No empty spots available
        dispatch(
          showSnackbar({
            message: "No empty parking spots available",
            severity: "error",
          })
        );
      }
      onClose();
    }
  };

  return (
    <Box
      sx={{
        backgroundColor: (theme) => alpha(theme.palette.background.paper, 0.4),
        backdropFilter: "blur(8px)",
        borderRadius: 3,
        width: "20rem",
        maxHeight: "50vh",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Typography
        variant="h5"
        component="h2"
        sx={{
          mt: 2,
          mb: 1,
          textAlign: "center",
          color: theme.palette.text.primary,
          fontWeight: 500,
        }}
      >
        Park Call
      </Typography>

      {/* Main content area with scroll */}
      <Box
        sx={{
          flex: "1 1 auto",
          overflowY: "auto",
          p: 2,
          mx: 1,
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: alpha(theme.palette.text.secondary, 0.05),
            borderRadius: "4px",
            marginTop: theme.spacing(1.75),
            marginBottom: theme.spacing(1.75),
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.text.secondary, 0.2),
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: alpha(theme.palette.text.secondary, 0.3),
            },
          },
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{
              p: 1,
              bgcolor: alpha(theme.palette.secondary.main, 0.15),
              borderRadius: 1,
            }}
          >
            <Stack>
              <Typography variant="subtitle1">Dynamic Park</Typography>
              <Typography variant="body2" color="text.secondary">
                Auto-assign to next available spot
              </Typography>
            </Stack>
            <IconButton
              onClick={handleDynamicPark}
              size="medium"
              sx={{
                color: theme.palette.common.white,
                backgroundColor: theme.palette.secondary.main,
                "&:hover": {
                  backgroundColor: theme.palette.secondary.dark,
                },
              }}
            >
              <LocalParkingIcon />
            </IconButton>
          </Stack>
          {parkQueues.map((queue) => (
            <Stack
              key={queue.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{
                p: 1,
                bgcolor: alpha(theme.palette.text.secondary, 0.15),
                borderRadius: 1,
              }}
            >
              <Stack>
                <Typography variant="subtitle1">
                  {queue.description || `Park ${queue.id}`} ({queue.number})
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {queue.status}
                </Typography>
              </Stack>
              <IconButton
                onClick={() => handlePark(queue.number, queue.description)}
                disabled={queue.status === "Occupied"}
                size="medium"
                sx={{
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.primary.light,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  "&.Mui-disabled": {
                    backgroundColor: alpha(theme.palette.primary.main, 0.3),
                  },
                }}
              >
                <LocalParkingIcon />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      </Box>

      {/* Footer with fixed position */}
      <Box
        sx={{
          flex: "0 0 auto",
          p: 2,
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        }}
      >
        <Button
          variant="contained"
          color="error"
          fullWidth
          onClick={onClose}
          sx={{
            textTransform: "none",
            fontSize: "16px",
          }}
        >
          Close
        </Button>
      </Box>
    </Box>
  );
};

export default CallParkOverlay;
